import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useKmClient } from '../../../lib/useKmClient.js';
import { useKmQuery } from '../hooks/useKmQuery.js';
import { KM_CONFIG } from '../../../api/config.js';
import ErrorDetails from '../components/ErrorDetails.jsx';

/* Corretto sul contratto reale (Collaudo_KM_TalentGPT_20260907_2.docx, §6.9,
   B.5, B.6):

   - GET /esperienze -> { esperienze: [...] }, non { items }. Ogni riga non
     ha scheda_id (solo nel dettaglio) -> tolto il filtro che lo richiedeva,
     altrimenti la lista sarebbe sempre vuota. Si sfogliano le esperienze,
     non "le schede": il link va su /esperienze/{esperienza_id}.

   - GET /kl/search -> { risultati: [...] }, non { items }. Ogni riga:
     { id, contenuto, esperienza_id, titolo, settore, dominio, ruolo,
     score, corpus, rrf_score, canali }. Nessun scheda_id: il link dei
     risultati va anch'esso su /esperienze/{esperienza_id}.

   - GET /kl/template richiede il parametro tipo* (obbligatorio), contesto
     facoltativo. Risponde un oggetto SINGOLO { template_id, titolo, tipo,
     file_path, istruzioni, versione, relevance } — non { sezioni: [...] }.
     Verificato dal vivo il 22/09 con entrambi i tipi confermati esistenti
     (template_documento ha solo 2 righe, ACL grp_sales): case_study e
     proposta_commerciale. file_path è un percorso interno (/drive/...),
     non un URL scaricabile — mostrato come testo, mai reso cliccabile,
     per non promettere un download che non funzionerebbe. "contesto" è
     un parametro reale che influenza "relevance" (mai sfruttato nella
     prima versione) — ora un campo libero, facoltativo.

   - POST /kl/ask: la risposta non ha un esempio JSON nel collaudo — resta
     letta in modo defensivo (risposta/text, fonti), non confermata. */

const TEMPLATE_TIPI = [
  { value: 'case_study', label: 'Case study' },
  { value: 'proposta_commerciale', label: 'Proposta commerciale' },
];

// Se esistono altri valori validi non li conosciamo ancora — da chiedere
// a Daniele/Monaco prima di aggiungerne altri qui, invece di indovinarli.

/* Marcatori di citazione (21/09): il README dice esplicitamente che
   l'interfaccia "deve" trasformare [EXP-xxxxxxxx] in riferimenti
   cliccabili verso fonti[].id — mai fatto prima, testo mostrato piatto.
   Il campo "id" dentro ogni fonte è una SCHEDA (corpus: "schede" nella
   risposta reale), non un'esperienza — il link va quindi verso
   /knowledge-management/schede/{id}, non verso esperienze/{id}. */
function renderRispostaConCitazioni(testo, fonti) {
  if (!testo) return null;
  const fontiPerCodice = new Map((fonti || []).map((f) => [f.fonte_id, f]));
  const parti = testo.split(/(\[[A-Z]+-[a-f0-9]+\])/g);
  return parti.map((parte, i) => {
    const codice = parte.match(/^\[([A-Z]+-[a-f0-9]+)\]$/)?.[1];
    if (!codice) return <React.Fragment key={i}>{parte}</React.Fragment>;
    const fonte = fontiPerCodice.get(codice);
    if (!fonte?.id) return <React.Fragment key={i}>{parte}</React.Fragment>; // marcatore senza riscontro: mostrato piatto, non inventato
    return (
      <Link key={i} to={`/knowledge-management/schede/${fonte.id}`} title={fonte.titolo || codice}>
        {parte}
      </Link>
    );
  });
}

export default function RicercaPage() {
  const kmClient = useKmClient('km');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null); // null = nessuna ricerca attiva, mostra l'elenco completo
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [askAnswer, setAskAnswer] = useState(null);
  const [askStatus, setAskStatus] = useState('idle');
  const [searchError, setSearchError] = useState(null);
  const [templateTipo, setTemplateTipo] = useState(TEMPLATE_TIPI[0].value);
  const [templateContesto, setTemplateContesto] = useState('');
  const [template, setTemplate] = useState(null);
  const [templateStatus, setTemplateStatus] = useState('idle');

  const { data: allExperiences, status: browseStatus, error: browseError, refetch: refetchBrowse } = useKmQuery(
    () => kmClient.get('/esperienze'),
    []
  );
  const browsableItems = Array.isArray(allExperiences?.esperienze) ? allExperiences.esperienze : [];

  const tooShort = query.trim().length > 0 && query.trim().length < KM_CONFIG.KL_SEARCH_MIN_QUERY_LEN;

  async function handleSearch(e) {
    e.preventDefault();
    if (tooShort || !query.trim()) return;
    setStatus('loading');
    setSearchError(null);
    try {
      const data = await kmClient.get('/kl/search', {
        query: { q: query.trim(), k: KM_CONFIG.KL_SEARCH_DEFAULT_K },
      });
      setResults(Array.isArray(data?.risultati) ? data.risultati : []);
      setStatus('success');
    } catch (err) {
      setSearchError(err);
      setStatus('error');
    }
  }

  function clearSearch() {
    setQuery('');
    setResults(null);
    setStatus('idle');
    setAskAnswer(null);
    setAskStatus('idle');
  }

  async function handleAsk() {
    if (!query.trim()) return;
    setAskStatus('loading');
    setAskAnswer(null);
    try {
      // Operazione LLM: timeout esteso gestito dal client (isLlmOperation)
      const data = await kmClient.post('/kl/ask', { domanda: query.trim() }, { isLlmOperation: true });
      setAskAnswer(data);
      setAskStatus('success');
    } catch {
      setAskStatus('error');
    }
  }

  async function fetchTemplate() {
    setTemplateStatus('loading');
    setTemplate(null);
    try {
      const data = await kmClient.get('/kl/template', {
        query: templateContesto.trim() ? { tipo: templateTipo, contesto: templateContesto.trim() } : { tipo: templateTipo },
      });
      setTemplate(data);
      setTemplateStatus('success');
    } catch {
      setTemplateStatus('error');
    }
  }


  const isSearchActive = results !== null;

  return (
    <div>
      <h1 className="section-title">Ricerca Knowledge Layer</h1>

      <form onSubmit={handleSearch} style={{ display: 'flex', gap: 'var(--space-3)', marginBottom: 'var(--space-5)' }}>
        <input
          className="field-input"
          placeholder="Cerca competenze, tecnologie, settori… (facoltativo, puoi anche sfogliare sotto)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <button type="submit" className="btn btn-primary" disabled={!query.trim() || tooShort}>Cerca</button>
        {/* "Chiedi al Knowledge Layer" nascosto il 23/09, su richiesta esplicita
            — non cancellato: handleAsk resta definito e funzionante, basta
            togliere il commento sotto per riattivarlo. */}
        {false && (
          <button type="button" className="btn btn-secondary" onClick={handleAsk} disabled={!query.trim()}>
            Chiedi al Knowledge Layer
          </button>
        )}
      </form>

      {tooShort && (
        <p style={{ color: 'var(--color-ink-muted)', fontSize: 'var(--font-size-sm)' }}>
          Inserisci almeno {KM_CONFIG.KL_SEARCH_MIN_QUERY_LEN} caratteri.
        </p>
      )}

      {/* Template per Sales — nascosto il 23/09, non richiesto per ora.
          Codice intatto: basta togliere {false && ...} per riattivarlo.
          Backend reale dietro (UC-7, get_template con ricerca vettoriale
          e ACL, confermato testato nel Collaudo) — non è vaporware, solo
          non richiesto adesso. */}
      {false && (
      <div className="card" style={{ marginBottom: 'var(--space-5)' }}>
        <h2 className="section-title" style={{ fontSize: 'var(--font-size-base)', marginBottom: 'var(--space-2)' }}>Template per Sales</h2>
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)', marginTop: 0, marginBottom: 'var(--space-3)' }}>
          Modello documentale più istruzioni di compilazione (UC-7), per chi prepara un'offerta o un caso di successo da mostrare a un cliente.
        </p>
        <ul style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)', marginTop: 0, marginBottom: 'var(--space-3)', paddingLeft: 18 }}>
          <li><strong>Case study</strong>: racconta un progetto già svolto, a partire dalle schede esperienza, per mostrare a un potenziale cliente un caso di successo comparabile.</li>
          <li><strong>Proposta commerciale</strong>: struttura per un'offerta da presentare a un cliente, con le sezioni già impostate (valore atteso, pricing, ecc.).</li>
          <li><strong>Contesto</strong> (facoltativo): descrivere in poche parole la situazione (es. "cliente utilities, migrazione cloud") per far valutare quanto il template proposto sia pertinente a quel caso specifico.</li>
          <li><strong>Pertinenza</strong>: percentuale calcolata dal sistema in base al contesto fornito — più alta, più il template è adatto alla situazione descritta.</li>
        </ul>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap', marginBottom: 'var(--space-3)' }}>
          <select className="field-select" style={{ maxWidth: 200 }} value={templateTipo} onChange={(e) => setTemplateTipo(e.target.value)}>
            {TEMPLATE_TIPI.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <input
            className="field-input"
            style={{ maxWidth: 320 }}
            placeholder="Contesto (facoltativo)"
            value={templateContesto}
            onChange={(e) => setTemplateContesto(e.target.value)}
          />
          <button type="button" className="btn btn-secondary" onClick={fetchTemplate} disabled={templateStatus === 'loading'}>
            {templateStatus === 'loading' ? 'Ricerca…' : 'Trova template'}
          </button>
        </div>

        {templateStatus === 'error' && (
          <p className="state-error" style={{ margin: 0 }}>Template non disponibile per questo tipo.</p>
        )}

        {template && (
          <div style={{ padding: 'var(--space-3)', background: 'var(--color-surface-alt, #F4F3EF)', borderRadius: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontSize: 'var(--font-size-sm)', fontWeight: 700 }}>{template.titolo || 'Template'}</h3>
              {typeof template.relevance === 'number' && (
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)' }}>
                  Pertinenza: {Math.round(template.relevance * 100)}%
                </span>
              )}
            </div>
            <p style={{ fontSize: 'var(--font-size-sm)', marginTop: 'var(--space-2)' }}>{template.istruzioni}</p>
            {template.file_path && (
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)', marginBottom: 0 }}>
                Percorso interno: <code>{template.file_path}</code> · versione {template.versione}
                <br />
                <span style={{ fontStyle: 'italic' }}>Non è un link scaricabile da qui — richiedere il file all'ambito che gestisce il drive interno.</span>
              </p>
            )}
          </div>
        )}
      </div>
      )}

      {askStatus === 'loading' && (
        <div className="card" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="skeleton-line" style={{ width: '90%' }} />
          <div className="skeleton-line" style={{ width: '70%' }} />
          <p style={{ color: 'var(--color-ink-muted)', fontSize: 'var(--font-size-xs)', marginTop: 'var(--space-2)' }}>
            L'elaborazione può richiedere fino a qualche minuto.
          </p>
        </div>
      )}
      {askStatus === 'error' && <p className="state-error">Non è stato possibile ottenere una risposta.</p>}
      {askStatus === 'success' && askAnswer && (
        <div className="card" style={{ marginBottom: 'var(--space-5)' }}>
          <h2 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>Risposta</h2>
          {/* Struttura confermata dal vivo il 18/09: { risposta, fonti:
              [{fonte_id, titolo, corpus, id}], n_frammenti, modello }. */}
          <p>{askAnswer.risposta ? renderRispostaConCitazioni(askAnswer.risposta, askAnswer.fonti) : 'Nessun contenuto restituito.'}</p>
          {Array.isArray(askAnswer.fonti) && askAnswer.fonti.length > 0 && (
            <div style={{ marginTop: 'var(--space-3)' }}>
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)', fontWeight: 600 }}>Fonti:</span>
              <ul style={{ margin: 'var(--space-1) 0 0', paddingLeft: 18, fontSize: 'var(--font-size-xs)', color: 'var(--color-ink-muted)' }}>
                {askAnswer.fonti.map((f) => (
                  <li key={f.fonte_id || f.id}>{f.titolo || f.fonte_id}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {isSearchActive && (
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-3)' }}>
            <h2 className="section-title" style={{ margin: 0, fontSize: 'var(--font-size-base)' }}>
              Risultati per "{query}"
            </h2>
            <button type="button" className="btn-link" onClick={clearSearch}>Torna a tutte le esperienze</button>
          </div>

          {status === 'loading' && <div className="card"><div className="skeleton-line" style={{ width: '60%' }} /></div>}
          {status === 'error' && <div className="state-error">Ricerca non riuscita.<ErrorDetails error={searchError} /></div>}
          {status === 'success' && results.length === 0 && (
            <div className="state-empty">Nessun risultato per "{query}". Prova a sfogliare l'elenco completo qui sotto.</div>
          )}
          {status === 'success' && results.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {results.map((r) => (
                <Link
                  key={r.id}
                  to={`/knowledge-management/esperienze/${r.esperienza_id}`}
                  className="card"
                  style={{ display: 'block', textDecoration: 'none', color: 'inherit' }}
                >
                  <div style={{ fontWeight: 600 }}>{r.titolo || 'Esperienza'}</div>
                  <p style={{ color: 'var(--color-ink-muted)', fontSize: 'var(--font-size-sm)', margin: 'var(--space-1) 0 0' }}>
                    {r.ruolo ? `${r.ruolo} · ` : ''}{r.contenuto}
                  </p>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <h2 className="section-title" style={{ fontSize: 'var(--font-size-base)' }}>
          Tutte le esperienze disponibili
        </h2>

        {browseStatus === 'loading' && (
          <div className="card">
            <div className="skeleton-line" style={{ width: '70%' }} />
            <div className="skeleton-line" style={{ width: '50%' }} />
            <div className="skeleton-line" style={{ width: '60%' }} />
          </div>
        )}

        {browseStatus === 'error' && (
          <div className="state-error">
            {browseError?.detail || "Non è stato possibile caricare l'elenco."}
            <div><button type="button" className="btn-link" onClick={refetchBrowse}>Riprova</button></div>
            <ErrorDetails error={browseError} />
          </div>
        )}

        {browseStatus === 'success' && browsableItems.length === 0 && (
          <div className="state-empty">Nessuna esperienza disponibile al momento.</div>
        )}

        {browseStatus === 'success' && browsableItems.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
            {browsableItems.map((exp) => (
              <Link
                key={exp.esperienza_id}
                to={`/knowledge-management/esperienze/${exp.esperienza_id}`}
                className="card"
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textDecoration: 'none', color: 'inherit' }}
              >
                <div>
                  <div style={{ fontWeight: 600 }}>{exp.cliente || 'Cliente non disponibile'}</div>
                  <p style={{ color: 'var(--color-ink-muted)', fontSize: 'var(--font-size-sm)', margin: 'var(--space-1) 0 0' }}>
                    {exp.ruolo || 'Ruolo non disponibile'} · {exp.periodo || '—'}
                  </p>
                </div>
                <span className={`badge ${exp.stato === 'chiusa' ? 'badge-neutral' : 'badge-success'}`}>
                  {exp.stato === 'chiusa' ? 'Chiusa' : 'In corso'}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
